import { FC, useState, useMemo } from "react";
import {
	Typography,
	Box,
	Card,
	CardContent,
	Button,
	TextField,
	Grid,
	Paper,
	IconButton,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TablePagination,
	Chip,
	Autocomplete,
	FormControl,
	InputLabel,
	Select,
	MenuItem,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import {
	useCups,
	useCreateCup,
	useUpdateCup,
	useDeleteCup,
	CupRequest,
	CupUpdateRequest,
} from "../../hooks/useCups";
import { useAnalytes } from "../../hooks/useAnalytes";
import { CupResponse, CupStatus } from "../../domain/cup.interface";
import { useUserStore } from "../../stores/user.store";
import GenericAlertModal from "../../components/modals/GenericAlertModal";

const CupsManagement: FC = () => {
	const { userInfo } = useUserStore();
	const [showCreateForm, setShowCreateForm] = useState(false);
	const [editingCup, setEditingCup] = useState<CupResponse | null>(null);
	const [search, setSearch] = useState("");
	const [page, setPage] = useState(0);
	const [pageSize, setPageSize] = useState(10);
	const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
	const [cupToDelete, setCupToDelete] = useState<string | null>(null);

	const [formData, setFormData] = useState<Omit<CupRequest, "createdBy">>({
		accountId: process.env.REACT_APP_ACCOUNT_ID || "",
		name: "",
		vendor: "",
		display: "",
		description: "",
		analytes: [],
		status: CupStatus.ACTIVE,
	});

	const { data: cupsResponse, isLoading, error } = useCups();
	const { data: analytesResponse } = useAnalytes();
	const createCup = useCreateCup();
	const updateCup = useUpdateCup();
	const deleteCup = useDeleteCup();

	const cups = cupsResponse?.body || [];
	const analytes = analytesResponse?.body || [];

	// Filter cups based on search
	const filteredCups = useMemo(
		() =>
			cups.filter(
				(cup: CupResponse) =>
					cup.name.toLowerCase().includes(search.toLowerCase()) ||
					cup.vendor.toLowerCase().includes(search.toLowerCase()) ||
					cup.description.toLowerCase().includes(search.toLowerCase()),
			),
		[cups, search],
	);

	// Paginate cups
	const paginatedCups = useMemo(
		() => filteredCups.slice(page * pageSize, (page + 1) * pageSize),
		[filteredCups, page, pageSize],
	);

	const handleCreateCup = async () => {
		if (!formData.name.trim() || !formData.vendor.trim()) {
			alert("Please fill in all required fields: Cup Name and Cup Vendor");
			return;
		}

		try {
			const requestData: CupRequest = {
				...formData,
				createdBy: userInfo?.id || "SYSTEM_USER",
			};
			await createCup.mutateAsync(requestData);
			resetForm();
			setShowCreateForm(false);
		} catch (error) {
			// eslint-disable-next-line no-console
			console.error("Error creating cup:", error);
			alert("Failed to create cup");
		}
	};

	const handleUpdateCup = async () => {
		if (!editingCup || !formData.name.trim() || !formData.vendor.trim()) {
			alert("Please fill in all required fields: Cup Name and Cup Vendor");
			return;
		}

		try {
			const requestData: CupUpdateRequest = {
				...formData,
				_id: editingCup._id,
				updatedBy: userInfo?.id || "SYSTEM_USER",
			};
			await updateCup.mutateAsync(requestData);
			resetForm();
			setEditingCup(null);
			setShowCreateForm(false);
		} catch (error) {
			// eslint-disable-next-line no-console
			console.error("Error updating cup:", error);
			alert("Failed to update cup");
		}
	};

	const handleDeleteCup = async () => {
		if (!cupToDelete) return;
		try {
			await deleteCup.mutateAsync(cupToDelete);
			setIsDeleteDialogOpen(false);
			setCupToDelete(null);
		} catch (error) {
			// eslint-disable-next-line no-console
			console.error("Error deleting cup:", error);
			alert("Failed to delete cup");
			setIsDeleteDialogOpen(false);
		}
	};

	const handleEdit = (cup: CupResponse) => {
		// Handle both cases: analytes as string IDs or as objects
		const analyteIds = Array.isArray(cup.analytes)
			? cup.analytes.map((a) => (typeof a === "string" ? a : a._id))
			: [];

		setEditingCup(cup);
		setFormData({
			accountId: cup.accountId,
			name: cup.name,
			vendor: cup.vendor,
			display: cup.display,
			description: cup.description,
			analytes: analyteIds,
			status: cup.status,
		});
		setShowCreateForm(true);
	};

	const resetForm = () => {
		setFormData({
			accountId: process.env.REACT_APP_ACCOUNT_ID || "",
			name: "",
			vendor: "",
			display: "",
			description: "",
			analytes: [],
			status: CupStatus.ACTIVE,
		});
	};

	const handleCancel = () => {
		resetForm();
		setShowCreateForm(false);
		setEditingCup(null);
	};

	if (error) {
		return (
			<Box sx={{ p: 3 }}>
				<Typography color="error">Error loading cups</Typography>
			</Box>
		);
	}

	return (
		<Box sx={{ p: 3 }}>
			<Typography variant="h4" gutterBottom>
				Cups Management
			</Typography>

			{/* Search and Create Button */}
			<Box sx={{ mb: 3, display: "flex", gap: 2 }}>
				<TextField
					placeholder="Search cups..."
					value={search}
					onChange={(e) => setSearch(e.target.value)}
					InputProps={{
						startAdornment: (
							<SearchIcon sx={{ mr: 1, color: "action.active" }} />
						),
					}}
					sx={{ flexGrow: 1 }}
				/>
				<Button
					variant="contained"
					startIcon={<AddIcon />}
					onClick={() => setShowCreateForm(!showCreateForm)}
				>
					{showCreateForm ? "Cancel" : "Create Cup"}
				</Button>
			</Box>

			{/* Create/Edit Form */}
			{showCreateForm && (
				<Card sx={{ mb: 3 }}>
					<CardContent>
						<Typography variant="h6" gutterBottom>
							{editingCup ? "Edit Cup" : "Create New Cup"}
						</Typography>
						<Grid container spacing={2}>
							<Grid item xs={12} md={6}>
								<TextField
									fullWidth
									label="Cup Name *"
									value={formData.name}
									onChange={(e) =>
										setFormData({ ...formData, name: e.target.value })
									}
								/>
							</Grid>
							<Grid item xs={12} md={6}>
								<TextField
									fullWidth
									label="Cup Vendor *"
									value={formData.vendor}
									onChange={(e) =>
										setFormData({ ...formData, vendor: e.target.value })
									}
								/>
							</Grid>
							<Grid item xs={12} md={6}>
								<TextField
									fullWidth
									label="Vendor Display Name"
									value={formData.display}
									onChange={(e) =>
										setFormData({
											...formData,
											display: e.target.value,
										})
									}
								/>
							</Grid>
							<Grid item xs={12} md={6}>
								<TextField
									fullWidth
									label="Description Display"
									value={formData.description}
									onChange={(e) =>
										setFormData({
											...formData,
											description: e.target.value,
										})
									}
								/>
							</Grid>
							<Grid item xs={12} md={6}>
								<FormControl fullWidth>
									<InputLabel>Status *</InputLabel>
									<Select
										value={formData.status}
										label="Status *"
										onChange={(e) =>
											setFormData({
												...formData,
												status: e.target.value as CupStatus,
											})
										}
									>
										<MenuItem value={CupStatus.ACTIVE}>Active</MenuItem>
										<MenuItem value={CupStatus.INACTIVE}>Inactive</MenuItem>
									</Select>
								</FormControl>
							</Grid>
							<Grid item xs={12}>
								<Autocomplete
									multiple
									options={analytes}
									getOptionLabel={(option) =>
										`${option.shortname} - ${option.scientificName}`
									}
									value={analytes.filter((a) =>
										formData.analytes?.includes(a._id),
									)}
									onChange={(_, newValue) => {
										setFormData({
											...formData,
											analytes: newValue.map((a) => a._id),
										});
									}}
									isOptionEqualToValue={(option, value) =>
										option._id === value._id
									}
									renderInput={(params) => (
										<TextField {...params} label="Analytes" />
									)}
								/>
							</Grid>
							<Grid item xs={12}>
								<Box sx={{ display: "flex", gap: 2 }}>
									<Button
										variant="contained"
										color="success"
										onClick={editingCup ? handleUpdateCup : handleCreateCup}
										disabled={createCup.isPending || updateCup.isPending}
									>
										{editingCup ? "Update" : "Create"}
									</Button>
									<Button variant="outlined" onClick={handleCancel}>
										Cancel
									</Button>
								</Box>
							</Grid>
						</Grid>
					</CardContent>
				</Card>
			)}

			{/* Cups Table */}
			<Paper>
				<TableContainer>
					<Table>
						<TableHead>
							<TableRow>
								<TableCell>Cup Name</TableCell>
								<TableCell>Vendor</TableCell>
								<TableCell>Vendor Display</TableCell>
								<TableCell>Description</TableCell> <TableCell>Status</TableCell>{" "}
								<TableCell>Analytes</TableCell>
								<TableCell>Actions</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{isLoading ? (
								<TableRow>
									<TableCell colSpan={7} align="center">
										Loading...
									</TableCell>
								</TableRow>
							) : paginatedCups.length === 0 ? (
								<TableRow>
									<TableCell colSpan={7} align="center">
										No cups found
									</TableCell>
								</TableRow>
							) : (
								paginatedCups.map((cup) => (
									<TableRow key={cup._id}>
										<TableCell>{cup.name}</TableCell>
										<TableCell>{cup.vendor}</TableCell>
										<TableCell>{cup.display}</TableCell>
										<TableCell>{cup.description}</TableCell>
										<TableCell>
											<Chip
												label={cup.status}
												size="small"
												color={
													cup.status === CupStatus.ACTIVE
														? "success"
														: "default"
												}
											/>
										</TableCell>
										<TableCell>
											<Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
												{cup.analytes.map((analyte) => {
													// Handle both string IDs and objects
													const analyteId =
														typeof analyte === "string" ? analyte : analyte._id;
													const analyteData =
														typeof analyte === "string"
															? analytes.find((a) => a._id === analyte)
															: analyte;
													return (
														<Chip
															key={analyteId}
															label={analyteData?.shortname || analyteId}
															size="small"
														/>
													);
												})}
											</Box>
										</TableCell>
										<TableCell>
											<IconButton
												size="small"
												onClick={() => handleEdit(cup)}
												color="primary"
											>
												<EditIcon />
											</IconButton>
											<IconButton
												size="small"
												onClick={() => {
													setCupToDelete(cup._id);
													setIsDeleteDialogOpen(true);
												}}
												color="error"
											>
												<DeleteIcon />
											</IconButton>
										</TableCell>
									</TableRow>
								))
							)}
						</TableBody>
					</Table>
				</TableContainer>
				<TablePagination
					component="div"
					count={filteredCups.length}
					page={page}
					onPageChange={(_, newPage) => setPage(newPage)}
					rowsPerPage={pageSize}
					onRowsPerPageChange={(e) => {
						setPageSize(parseInt(e.target.value, 10));
						setPage(0);
					}}
					rowsPerPageOptions={[5, 10, 25, 50]}
				/>
			</Paper>

			{isDeleteDialogOpen && (
				<GenericAlertModal
					title="Delete Cup"
					description="Are you sure you want to delete this registry?"
					submitText="Confirm"
					cancelText="Cancel"
					isLoading={deleteCup.isPending}
					onClick={handleDeleteCup}
					onCancel={() => {
						setIsDeleteDialogOpen(false);
						setCupToDelete(null);
					}}
				/>
			)}
		</Box>
	);
};

export default CupsManagement;
